<script lang="ts">
import { defineComponent, onMounted, ref, watch, type PropType } from 'vue'
import { INSPECTION_METHODS } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/step1/inspectionMethod'
import { INSPECTION_METHOD_LABELS } from '@/constants/ui/inspection/step1/inspectionMethodLabel'
import { type InspectionMethodType } from '@/types/ui/inspection/inspectionMethodTypes'
import type { InputSelectOption } from '@/types/ui/inspection/common/inspectionSelectOptions'
import { EXTERIOR_RISK_TYPE_LABELS } from '@/constants/ui/inspection/step1/exteriorRiskTypeLabel'
import { EXTERIOR_RISK_TYPE } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/step1/exteriorRiskType'
import LabeledCard from '@/components/inspection/common/LabeledCard.vue'
import { IMAGE_COUNT_LIMIT_FOR_INSPECTION } from '@/constants/accessor/api/common/imageCountLimit'
import { S3_DIRECTORY_NAMES } from '@/constants/accessor/s3/directoryName'
import { useInspectionResultStore } from '@/stores/inspectionResultUpsertStore'
import type { ExteriorRiskType } from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/step1'
import PhotoDBOperator from '@/accessors/indexedDB/photoDBOperator'
import { useDisasterStore } from '@/stores/disasterStore'

export interface InspectionFieldData {
  index?: number
  title: string
  inputSelectOption: InputSelectOption[]
}

export default defineComponent({
  name: 'InspectionStep1',
  components: {
    LabeledCard
  },
  props: {
    inspectionFieldData: {
      type: Object as PropType<InspectionFieldData>,
      required: true,
      default: () => ({
        inputSelectOption: EXTERIOR_RISK_TYPE_LABELS,
        title: '一見して危険と判定される'
      })
    },
    targetActionKey: {
      type: String,
      required: true
    },
    s3DirectoryName: {
      type: String,
      required: true
    },
    triggerValidate: {
      type: Boolean,
      default: false
    },
    saveToStoreFlag: {
      type: Boolean,
      default: false
    }
  },
  emit: ['updateUnfilledList', 'savedInspectionResultStore', 'updateSelectedExteriorRiskType'],
  setup(props, { emit }) {
    const inspectionResultStore = useInspectionResultStore()
    const disasterStore = useDisasterStore()

    /** 一見して危険かどうかと危険種別 */
    const exteriorRiskType = ref<ExteriorRiskType>(EXTERIOR_RISK_TYPE.UNSELECTED)

    /** 危険種別その他のコメント */
    const exteriorRiskOther = ref<string>('')

    /** 調査方法 */
    const selectedInspectionMethod = ref<InspectionMethodType>(INSPECTION_METHODS.EXTERNAL)

    /** 画像リスト */
    const imageList = ref<File[]>([])

    /** 外観調査画像のS3ディレクトリパス */
    const imageDirectory = ref<string>(
      [
        disasterStore.govCode,
        disasterStore.disasterId,
        inspectionResultStore.commonResultSummary.assessmentId,
        S3_DIRECTORY_NAMES.EXTERIOR
      ].join('/') + '/'
    )

    /** 外観調査画像のファイル名リスト */
    const imagePathList = ref<string[]>([])

    /** 必須項目の未入力リスト */
    const unfilledList = ref<string[]>([props.inspectionFieldData.title])

    /**
     * 画像パスリストを更新
     * @param payload imagePathList: 画像パスリスト, key: 対象の調査項目識別用キー
     */
    const updateImagePathList = (payload: { imagePathList: string[]; key: string }): void => {
      imagePathList.value = [...payload.imagePathList]
    }

    /**
     * 画像リストを更新
     * @param payload imagePathList: 画像リスト, key: 対象の調査項目識別用キー
     */
    const updateImageList = (payload: { imageList: File[] }): void => {
      imageList.value = [...payload.imageList]
    }

    /**
     * リアクティブ変数にStoreの値をセット
     * @param なし
     */
    const setRefsFromStore = async (): Promise<void> => {
      // ディレクトリパスは初回から確定しているため、ここでは初期化せずref変数の宣言時に初期化しておく
      selectedInspectionMethod.value = inspectionResultStore.step1.inspectionMethod
      exteriorRiskType.value = inspectionResultStore.step1.exterior.exteriorRiskType
      exteriorRiskOther.value = inspectionResultStore.step1.exterior.exteriorRiskOther
      imagePathList.value = inspectionResultStore.step1.exterior.imageFilePathList

      // 画像はindexedDBから取得
      const tmpItems = await PhotoDBOperator.getItems(imageDirectory.value, imagePathList.value)

      // Fileオブジェクトに変換した上でrefに格納
      imageList.value = [
        ...tmpItems
          .map(e => {
            if (e === undefined || e.arrayBuffer === undefined) return

            const blob = new Blob([e.arrayBuffer], { type: e.mime })
            const file = new File([blob], e.fileName, { type: e.mime })
            return file
          })
          .filter((file): file is File => file !== undefined)
      ]
    }

    watch(
      exteriorRiskType,
      newValue => {
        if (newValue === EXTERIOR_RISK_TYPE.UNSELECTED) {
          unfilledList.value = [props.inspectionFieldData.title]
        } else {
          unfilledList.value = []
        }
        emit('updateUnfilledList', unfilledList.value)

        emit('updateSelectedExteriorRiskType', newValue)
      },
      { immediate: true }
    )

    watch(
      () => props.triggerValidate,
      newValue => {
        if (newValue) {
          // バリデーションなし
          emit('onValidationResult', true)
        }
      }
    )

    watch(
      () => props.saveToStoreFlag,
      newValue => {
        if (newValue) {
          // 各入力値をstoreに保存
          const step1Param = {
            exterior: {
              exteriorRiskType: exteriorRiskType.value,
              exteriorRiskOther: exteriorRiskOther.value,
              imageDirectory: imageDirectory.value,
              imageFilePathList: imagePathList.value
            },
            inspectionMethod: selectedInspectionMethod.value
          }
          inspectionResultStore.setStep1(step1Param)
          emit('savedInspectionResultStore')
        }
      }
    )

    onMounted(() => {
      // Storeから各入力値を取得し、ref変数に反映
      setRefsFromStore()
    })

    return {
      exteriorRiskType,
      exteriorRiskOther,
      IMAGE_COUNT_LIMIT_FOR_INSPECTION,
      EXTERIOR_RISK_TYPE_LABELS,
      selectedInspectionMethod,
      imageList,
      imagePathList,
      INSPECTION_METHOD_LABELS,
      EXTERIOR_RISK_TYPE,
      S3_DIRECTORY_NAMES,
      updateImagePathList,
      updateImageList
    }
  }
})
</script>

<template>
  <div class="container mt-5">
    <!-- タイトル部分 -->
    <div class="d-flex justify-content-between border-bottom border-dark mb-5 pb-4">
      <h1 class="text-xl font-weight-bold flex-grow">
        調査 Step1 <span class="text-danger">【必須】</span>
      </h1>
    </div>
    <!-- 調査方法カード -->
    <div class="border border-secondary rounded-lg p-4 mb-4 bg-white shadow-sm">
      <h2 class="fw-bold mb-4 text-black">調査方法</h2>
      <div class="d-flex align-items-center gap-4">
        <b-form-radio-group
          v-model="selectedInspectionMethod"
          name="inspectionMethod"
          :options="INSPECTION_METHOD_LABELS"
          switches
          class="fs-4"
        ></b-form-radio-group>
      </div>
    </div>
    <LabeledCard
      v-model="exteriorRiskType"
      :inspectionFieldData="inspectionFieldData"
      :targetActionKey="targetActionKey"
      :s3DirectoryName="S3_DIRECTORY_NAMES.EXTERIOR"
      :imageCount="imageList.length"
      :imageCountLimit="IMAGE_COUNT_LIMIT_FOR_INSPECTION"
      :imageList="imageList"
      :imagePathList="imagePathList"
      @updateImagePathList="updateImagePathList"
      @updateImageList="updateImageList"
    >
      <template #input-field>
        <b-form-select
          v-model="exteriorRiskType"
          :options="inspectionFieldData.inputSelectOption"
          class="fs-4 shadow"
        />
        <!-- "その他"選択時のみ入力ボックスを表示 -->
        <div v-if="exteriorRiskType === EXTERIOR_RISK_TYPE.OTHER">
          <form autocomplete="off" @submit.prevent>
            <b-form-input v-model="exteriorRiskOther" class="fs-4" />
          </form>
        </div>
      </template>
    </LabeledCard>
  </div>
</template>

<style scoped>
:deep(.form-select option) {
  font-size: 1.2rem;
}

:deep(.btn-group-md .form-check):not(:first-child) {
  margin-left: 1.5rem;
}

:deep(.btn-group-md .form-check-input) {
  margin-right: 0.8rem;
}
</style>
